import React, { useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  SimpleGrid,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { FiX } from "react-icons/fi";

const BusySpinner = () => <Spinner size="lg" thickness="3px" color="blue.500" />;
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { Pagination } from "newui/components/Pagination";
import { usePagination } from "newui/components/Pagination/usePagination";
import { TableColumnFilter } from "newui/components/Filters/TableColumnFilter";
import { TextSearchFilter } from "newui/components/Filters/TextSearchFilter";
import { DateRangeFilter, DateRangeValue } from "newui/components/Filters/DateRangeFilter";
import { SelectOption } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import { RfxDocumentFilters, RfxDocumentRow } from "../types/rfx-document.types";
import { DocumentMobileCard } from "./DocumentMobileCard";
import { SortableDocumentRow } from "./SortableDocumentRow";

interface DocumentTableProps {
  categoryOptions: SelectOption[];
  documents: RfxDocumentRow[];
  filteredDocuments: RfxDocumentRow[];
  filters: RfxDocumentFilters;
  /** When true, the "Clear filters" button is enabled in the table toolbar. */
  hasActiveFilters?: boolean;
  /** Hide the Revision column entirely. Useful in creation flows where every
   * row is implicitly the first revision. */
  hideRevision?: boolean;
  isAllSelected: boolean;
  /** When true, dim the table and show a spinner overlay. Used while uploads
   * are in-flight or the table is refetching after a mutation so the user
   * sees stale rows are being replaced rather than thinking the upload had
   * no effect. */
  isBusy?: boolean;
  isIndeterminate: boolean;
  selectedIds: Set<number>;
  onClearSelection: () => void;
  onDeleteRequest: (doc: RfxDocumentRow) => void;
  onFilterChange: <K extends keyof RfxDocumentFilters>(
    key: K,
    value: RfxDocumentFilters[K]
  ) => void;
  onReorder: (orderedIds: number[]) => void;
  onSelectAll: () => void;
  onToggleSelect: (id: number) => void;
  onUpdate: (docId: number, data: { category_id?: number; external_revision?: string }) => Promise<void>;
  /** Reset every column filter to its empty state. */
  onClearFilters?: () => void;
}

export const DocumentTable = ({
  documents,
  filteredDocuments,
  filters,
  categoryOptions,
  selectedIds,
  isAllSelected,
  isIndeterminate,
  onFilterChange,
  onToggleSelect,
  onSelectAll,
  onClearSelection,
  onUpdate,
  onDeleteRequest,
  onReorder,
  hideRevision = false,
  isBusy = false,
  hasActiveFilters = false,
  onClearFilters,
}: DocumentTableProps) => {
  const pagination = usePagination({
    totalItems: filteredDocuments.length,
    initialRowsPerPage: 25,
  });

  const paginatedDocs = useMemo(
    () => filteredDocuments.slice(pagination.startIndex - 1, pagination.endIndex),
    [filteredDocuments, pagination.startIndex, pagination.endIndex]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = filteredDocuments.findIndex((d) => d.id === active.id);
    const newIndex = filteredDocuments.findIndex((d) => d.id === over.id);
    const reordered = arrayMove(filteredDocuments, oldIndex, newIndex);
    onReorder(reordered.map((d) => d.id));
  };

  // Build uploader filter options from documents
  const uploaderOptions: SelectOption[] = useMemo(() => {
    const map = new Map<number, string>();
    documents.forEach((d) => {
      if (d.uploadedById && !map.has(d.uploadedById)) {
        map.set(d.uploadedById, d.uploadedBy);
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [documents]);

  const handleSelectAllChange = () => {
    if (isAllSelected) {
      onClearSelection();
    } else {
      onSelectAll();
    }
  };

  return (
    <Box data-testid="document-table" position="relative">
      {hasActiveFilters && onClearFilters && (
        <Box display="flex" justifyContent="flex-start" mb={2}>
          <Button
            size="xs"
            variant="ghost"
            colorScheme="blue"
            color="primary.500"
            leftIcon={<FiX />}
            onClick={onClearFilters}
            data-testid="document-table-clear-filters"
          >
            Clear filters
          </Button>
        </Box>
      )}
      {isBusy && (
        <Box
          position="absolute"
          inset={0}
          bg="whiteAlpha.700"
          zIndex={2}
          display="flex"
          alignItems="center"
          justifyContent="center"
          data-testid="document-table-busy-overlay"
        >
          <BusySpinner />
        </Box>
      )}
      {/* ─── Mobile (< md): stacked card list ─────────────────────────────
          Reorder is dropped here (touch DnD is awkward and reorder is a
          power-user action better done on desktop). Filters mirror the
          desktop column-header filters but live in a compact grid above
          the cards so they remain reachable. */}
      <Box display={{ base: "block", md: "none" }}>
        <Stack spacing={2} mb={3}>
          <Flex align="center" gap={2}>
            <Checkbox
              isChecked={isAllSelected}
              isIndeterminate={isIndeterminate}
              onChange={handleSelectAllChange}
              data-testid="document-table-select-all-mobile"
            >
              <Text fontSize="sm" color="gray.600">
                Select all
              </Text>
            </Checkbox>
            <Text fontSize="xs" color="gray.500" ml="auto">
              {filteredDocuments.length} document
              {filteredDocuments.length === 1 ? "" : "s"}
            </Text>
          </Flex>
          <SimpleGrid columns={2} spacing={2}>
            <TextSearchFilter
              label="Filename"
              value={filters.filename}
              onChange={(v) => onFilterChange("filename", v)}
              placeholder="Search filename..."
            />
            <TableColumnFilter
              label="Category"
              options={categoryOptions}
              value={filters.categoryIds}
              onChange={(v) => onFilterChange("categoryIds", v as number[])}
              isMulti
            />
            <TextSearchFilter
              label="Revision"
              value={filters.revision}
              onChange={(v) => onFilterChange("revision", v)}
              placeholder="Search revision..."
            />
            <DateRangeFilter
              label="Uploaded"
              value={filters.dateRange}
              onChange={(v) => onFilterChange("dateRange", v as DateRangeValue)}
            />
            <TableColumnFilter
              label="Uploaded By"
              options={uploaderOptions}
              value={filters.uploadedByIds}
              onChange={(v) => onFilterChange("uploadedByIds", v as number[])}
              isMulti
            />
          </SimpleGrid>
        </Stack>
        <Stack spacing={2}>
          {paginatedDocs.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Text color="gray.500">No documents found</Text>
            </Box>
          ) : (
            paginatedDocs.map((doc) => (
              <DocumentMobileCard
                key={doc.id}
                doc={doc}
                isSelected={selectedIds.has(doc.id)}
                categoryOptions={categoryOptions}
                hideRevision={hideRevision}
                onToggleSelect={onToggleSelect}
                onUpdate={onUpdate}
                onDeleteRequest={onDeleteRequest}
              />
            ))
          )}
        </Stack>
      </Box>

      {/* ─── Desktop (>= md): full table with drag-reorder ─────────────── */}
      <TableContainer display={{ base: "none", md: "block" }}>
        <Table
          variant="simple"
          size="sm"
          sx={{
            "th, td": {
              paddingInlineStart: "calc(var(--chakra-space-4) - 1px)",
              paddingInlineEnd: "calc(var(--chakra-space-4) - 1px)",
            },
            // Dark grey header text (rather than near-black) — applies to plain
            // <Th>Size</Th> labels AND propagates into the filter MenuButton
            // labels via colour inheritance.
            "thead th, thead th button, thead th [role='menuitem']": {
              color: "gray.500",
            },
            // Drag-handle column (1st) butts up against the checkbox column (2nd) — zero the gap.
            "th:nth-of-type(1), td:nth-of-type(1)": {
              paddingInlineEnd: 0,
            },
            "th:nth-of-type(2), td:nth-of-type(2)": {
              paddingInlineStart: 0,
            },
          }}
        >
          <Thead>
            <Tr>
              <Th width="20px" />
              <Th>
                <Checkbox
                  isChecked={isAllSelected}
                  isIndeterminate={isIndeterminate}
                  onChange={handleSelectAllChange}
                  data-testid="document-table-select-all"
                />
              </Th>
              <Th>
                <TextSearchFilter
                  label="Filename"
                  value={filters.filename}
                  onChange={(v) => onFilterChange("filename", v)}
                  placeholder="Search filename or extension..."
                />
              </Th>
              <Th>Size</Th>
              <Th>
                <TableColumnFilter
                  label="Category"
                  options={categoryOptions}
                  value={filters.categoryIds}
                  onChange={(v) => onFilterChange("categoryIds", v as number[])}
                  isMulti
                />
              </Th>
              <Th>
                <TextSearchFilter
                  label="Revision"
                  value={filters.revision}
                  onChange={(v) => onFilterChange("revision", v)}
                  placeholder="Search revision..."
                />
              </Th>
              <Th>
                <DateRangeFilter
                  label="Uploaded"
                  value={filters.dateRange}
                  onChange={(v) => onFilterChange("dateRange", v as DateRangeValue)}
                />
              </Th>
              <Th>
                <TableColumnFilter
                  label="Uploaded By"
                  options={uploaderOptions}
                  value={filters.uploadedByIds}
                  onChange={(v) => onFilterChange("uploadedByIds", v as number[])}
                  isMulti
                />
              </Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={paginatedDocs.map((d) => d.id)}
              strategy={verticalListSortingStrategy}
            >
              <Tbody>
                {paginatedDocs.length === 0 ? (
                  <Tr>
                    <Th colSpan={9} textAlign="center" py={8}>
                      <Text color="gray.500">No documents found</Text>
                    </Th>
                  </Tr>
                ) : (
                  paginatedDocs.map((doc) => (
                    <SortableDocumentRow
                      key={doc.id}
                      doc={doc}
                      isSelected={selectedIds.has(doc.id)}
                      categoryOptions={categoryOptions}
                      hideRevision={hideRevision}
                      onToggleSelect={onToggleSelect}
                      onUpdate={onUpdate}
                      onDeleteRequest={onDeleteRequest}
                    />
                  ))
                )}
              </Tbody>
            </SortableContext>
          </DndContext>
        </Table>
      </TableContainer>
      <Pagination
        currentPage={pagination.currentPage}
        rowsPerPage={pagination.rowsPerPage}
        totalItems={filteredDocuments.length}
        onPageChange={pagination.setCurrentPage}
        onRowsPerPageChange={pagination.setRowsPerPage}
        testId="document-table-pagination"
      />
    </Box>
  );
};
